import React from "react";
import { Link } from "react-router-dom";
import DemoButton from "../shared/DemoButton";
import StatsBar from "../shared/StatsBar";
import FAQSection from "./FAQSection";
import "./PricingDesktop.css";

const PLANS = [
  {
    id: "intelligence",
    name: "Highwatch Intelligence",
    desc: "AI platform for growing businesses to automate operations and unify company knowledge.",
    price: "$1,990",
    priceSuffix: "/month",
    cta: { to: "/Contact", label: "Get a demo", style: "outline" },
    iconClass: "is-teal",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" strokeWidth="1.6" />
        <path d="M9 12h6M12 9v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
    features: [
      "Up to 25 users",
      "Up to 10 business system integrations",
      "Unified search across connected systems",
      "Context-aware AI that understands your business",
      "Workflow automation & action execution",
      "Enterprise-grade security",
      "Guided onboarding & deployment",
    ],
    note: "Perfect for organizations looking to deploy AI securely without replacing their existing tools.",
  },
  {
    id: "insurance",
    name: "Highwatch Insurance",
    desc: "Insurance AI platform for faster claims, smarter decisions, and modern operations.",
    price: "$4,990",
    priceSuffix: "/month",
    popular: true,
    cta: { to: "/Contact", label: "Get a demo", style: "solid" },
    iconClass: "is-purple",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 3.5l7 3v5.2c0 4.3-2.9 8.2-7 9.3-4.1-1.1-7-5-7-9.3V6.5l7-3z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      </svg>
    ),
    features: [
      "Up to 25 users",
      "Up to 10 business system integrations",
      "Layer 0: Core Intelligence",
      "Layer 1: Intelligence Applications (Parsing, Risk Summary & Triage)",
      "Layer 2: Operations (Claims Inbox, Smart Search & Activity Timeline)",
      "Layer 3: Governance & Insights (Compliance Copilot & ROI Dashboard)",
      "Enterprise-Grade Security",
      "Guided Onboarding & Deployment",
    ],
    note: "Perfect for insurance organizations looking to modernize claims operations with secure, AI-powered intelligence.",
  },
  {
    id: "enterprise",
    name: "Highwatch Enterprise",
    desc: "Custom enterprise AI platform built for complex operations, security, and unlimited scale.",
    price: "Custom",
    priceSuffix: "",
    cta: { to: "/Contact", label: "Talk to sales", style: "outline" },
    iconClass: "is-blue",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 20V8l8-4 8 4v12" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M9 20v-6h6v6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
    ),
    features: [
      "Unlimited Users",
      "Unlimited Business System Integrations",
      "Custom AI Applications & Automation Solutions",
      "Industry-Specific Workflows & Intelligence",
      "Advanced Security & Compliance",
      "Private Cloud, On-Premise & Hybrid Deployment",
      "SSO & Enterprise Identity Management",
      "Dedicated Customer Success",
      "Custom Onboarding & Implementation",
    ],
    note: "Perfect for enterprises looking to build custom AI applications, automate complex business processes, and transform operations at scale.",
  },
];

const COMPARE_ROWS = [
  { feature: "Users", values: ["Up to 25", "Up to 25", "Unlimited"] },
  { feature: "Business System Integrations", values: ["Up to 10", "Up to 10", "Unlimited"] },
  { feature: "AI Platform", values: ["check", "check", "check"] },
  { feature: "Workflow Automation", values: ["check", "check", "check"] },
  { feature: "Unified Search", values: ["check", { type: "check", note: "Smart Search" }, "check"] },
  { feature: "Core Intelligence", values: ["check", { type: "check", note: "Layer 0" }, "check"] },
  { feature: "Intelligence Applications", values: ["x", { type: "check", note: "Layer 1" }, "check"] },
  { feature: "Operations Applications", values: ["x", { type: "check", note: "Layer 2" }, "check"] },
  { feature: "Governance & Insights", values: ["x", { type: "check", note: "Layer 3" }, "check"] },
  { feature: "Custom AI Applications", values: ["x", "x", "check"] },
  { feature: "Industry-Specific Workflows", values: ["x", { type: "text", label: "Insurance" }, "check"] },
  { feature: "Enterprise-Grade Security", values: ["check", "check", "check"] },
  { feature: "Guided Onboarding & Deployment", values: ["check", "check", "check"] },
  { feature: "Custom Onboarding & Implementation", values: ["x", "x", "check"] },
  { feature: "Private Cloud / On-Premise / Hybrid", values: ["x", "x", "check"] },
  { feature: "SSO & Enterprise Identity Management", values: ["x", "x", "check"] },
  { feature: "Dedicated Customer Success", values: ["x", "x", "check"] },
];

function CellValue({ value }) {
  if (value === "check" || value?.type === "check") {
    return (
      <span className="hw-pricing-compare__cell">
        <span className="hw-pricing-compare__check" aria-hidden="true">
          ✓
        </span>
        {value?.note ? <span className="hw-pricing-compare__note-inline">({value.note})</span> : null}
      </span>
    );
  }
  if (value === "x") {
    return (
      <span className="hw-pricing-compare__x" aria-label="Not included">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path
            d="M3 3l8 8M11 3L3 11"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      </span>
    );
  }
  if (value?.type === "text") {
    return <span className="hw-pricing-compare__text">{value.label}</span>;
  }
  return <span className="hw-pricing-compare__text">{value}</span>;
}

export default function PricingDesktop() {
  return (
    <div className="hw-pricing-desktop">
      <section className="hw-pricing-hero">
        <div className="hw-pricing-hero__inner">
          <div className="hw-pricing-hero__copy">
            <div className="hw-pricing-hero__badge">
              <span className="hw-pricing-hero__badge-dot" />
              BUILT FOR REGULATED INSURANCE ORGANIZATIONS
            </div>
            <h1>
              One platform. Three ways
              <br />
              to scale with AI.
            </h1>
            <p className="hw-pricing-hero__sub">
              From growing companies to global enterprises, Highwatch scales with your
              organization, security requirements, and operational complexity.
            </p>
            <div className="hw-pricing-hero__actions">
              <DemoButton to="/Contact" />
              <Link to="/Contact" className="hw-pricing-btn hw-pricing-btn--ghost">
                Request Pricing <span aria-hidden="true">→</span>
              </Link>
            </div>
            <p className="hw-pricing-hero__fine">
              No credit card required · Built for SOC 2 / GDPR-ready environments
            </p>
          </div>
          <img
            src="/images/Overview.svg"
            alt="Highwatch Overview dashboard"
            className="hw-pricing-hero__image"
          />
        </div>
      </section>

      <section className="hw-pricing-plans">
        <div className="hw-pricing-plans__intro">
          <h2>Plans that scale with your organization.</h2>
          <p>
            Every Highwatch plan includes enterprise-grade security, contextual AI, and
            compliance-ready auditability. Start with your core systems, expand as your
            workflows evolve.
          </p>
        </div>

        <div className="hw-pricing-plans__grid">
          {PLANS.map((plan) => (
            <article
              key={plan.id}
              className={`hw-pricing-card ${plan.popular ? "is-popular" : ""}`}
            >
              {plan.popular ? <span className="hw-pricing-card__badge">MOST POPULAR</span> : null}
              <div className={`hw-pricing-card__icon ${plan.iconClass}`}>{plan.icon}</div>
              <h3>{plan.name}</h3>
              <p className="hw-pricing-card__desc">{plan.desc}</p>
              <div className="hw-pricing-card__price">
                <strong>{plan.price}</strong>
                {plan.priceSuffix ? <span>{plan.priceSuffix}</span> : null}
              </div>
              <Link
                to={plan.cta.to}
                className={`hw-pricing-btn ${
                  plan.cta.style === "solid" ? "hw-pricing-btn--solid" : "hw-pricing-btn--outline"
                }`}
              >
                {plan.cta.label}
              </Link>
              <ul>
                {plan.features.map((item) => (
                  <li key={item}>
                    <span aria-hidden="true">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="hw-pricing-card__note">{plan.note}</p>
            </article>
          ))}
        </div>

        <p className="hw-pricing-plans__foot">
          Every plan includes compliance-grade security, real-time data sync, and continuous
          updates. Pricing scales transparently with usage, no surprises, no hidden fees.
        </p>
      </section>

      <section className="hw-pricing-compare">
        <div className="hw-pricing-compare__table" role="table" aria-label="Compare plans">
          <div className="hw-pricing-compare__row hw-pricing-compare__row--head" role="row">
            <div className="hw-pricing-compare__feature" role="columnheader">
              <h2>Compare plans</h2>
            </div>
            <div className="hw-pricing-compare__plan" role="columnheader">
              <strong>Highwatch Intelligence</strong>
              <span>$1,990 /month</span>
              <Link to="/Contact" className="hw-pricing-btn hw-pricing-btn--outline">
                Get a demo
              </Link>
            </div>
            <div className="hw-pricing-compare__plan is-featured" role="columnheader">
              <strong>Highwatch Insurance</strong>
              <span>$4,990 /month</span>
              <Link to="/Contact" className="hw-pricing-btn hw-pricing-btn--outline">
                Get a demo
              </Link>
            </div>
            <div className="hw-pricing-compare__plan" role="columnheader">
              <strong>Highwatch Enterprise</strong>
              <span>Custom</span>
              <Link to="/Contact" className="hw-pricing-btn hw-pricing-btn--outline">
                Talk to sales
              </Link>
            </div>
          </div>

          {COMPARE_ROWS.map((row) => (
            <div className="hw-pricing-compare__row" role="row" key={row.feature}>
              <div className="hw-pricing-compare__feature" role="cell">
                {row.feature}
              </div>
              {row.values.map((value, i) => (
                <div
                  className={`hw-pricing-compare__plan ${i === 1 ? "is-featured" : ""}`}
                  role="cell"
                  key={`${row.feature}-${i}`}
                >
                  <CellValue value={value} />
                </div>
              ))}
            </div>
          ))}
        </div>
        <p className="hw-pricing-compare__note">
          Find the right Highwatch platform for your organization today, with the flexibility to
          scale as your business grows.
        </p>
      </section>

      <StatsBar />
      <FAQSection variant="pricing" showCta />
    </div>
  );
}
